import { SnowflakeIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ACTIVITIES__NEW__ } from "@/lib/constants";

/**
 * The popup is shown from 1 December 2026 until the end of 1 January 2027.
 * Bump the dates, the storage key and the hours below for the next season.
 * Months are zero-based: 11 = December, 0 = January.
 */
const SHOW_FROM = new Date(2026, 9, 1);
const SHOW_UNTIL = new Date(2027, 0, 2);
const STORAGE_KEY = "holiday-hours-2026-dismissed";

type Day = {
  date: string;
  /** Opening hours. Leave empty for a closed day. */
  hours?: string;
  note?: string;
};

type Venue = {
  key: string;
  name: string;
  /** Background colour of the card header. */
  color: string;
  /** Text colour on top of `color`. */
  foreground: string;
  days: Day[];
};

const venues: Venue[] = [
  {
    key: "bowling",
    name: "Bowling",
    color: ACTIVITIES__NEW__.bowling.brand.primaryColor,
    foreground: ACTIVITIES__NEW__.bowling.brand.primaryForegroundColor,
    days: [
      { date: "24.12." },
      {
        date: "25.12.",
        hours: "14:00 – 00:00 Uhr",
        note: "33 € pro Bahn/Stunde + Leihschuhe",
      },
      {
        date: "26.12.",
        hours: "12:00 – 00:00 Uhr",
        note: "33 € pro Bahn/Stunde + Leihschuhe",
      },
      {
        date: "31.12.",
        hours: "14:00 – 22:00 Uhr",
        note: "35 € pro Bahn/Stunde",
      },
      { date: "01.01." },
    ],
  },
  {
    key: "minigolf",
    name: "3D Schwarzlicht-Minigolf",
    color: ACTIVITIES__NEW__.minigolf.brand.primaryColor,
    foreground: ACTIVITIES__NEW__.minigolf.brand.primaryForegroundColor,
    days: [
      { date: "24.12." },
      {
        date: "25.12.",
        hours: "14:00 – 00:00 Uhr",
        note: "Letzter Einlass 22:00 Uhr",
      },
      {
        date: "26.12.",
        hours: "12:00 – 00:00 Uhr",
        note: "Letzter Einlass 22:00 Uhr",
      },
      {
        date: "31.12.",
        hours: "14:00 – 21:00 Uhr",
        note: "Letzter Einlass 20:00 Uhr",
      },
      { date: "01.01." },
    ],
  },
  {
    key: "aposto",
    name: "Aposto",
    color: "#ffffff",
    foreground: "#000000",
    days: [
      { date: "24.12." },
      { date: "25.12.", hours: "15:00 – 00:00 Uhr" },
      { date: "26.12.", hours: "14:00 – 00:00 Uhr" },
      { date: "31.12.", hours: "15:00 – 22:00 Uhr" },
      { date: "01.01." },
    ],
  },
];

function isInShowPeriod() {
  const now = new Date();
  return now >= SHOW_FROM && now < SHOW_UNTIL;
}

export function HolidayHoursDialog() {
  const [open, setOpen] = useState(false);

  // Decided on the client only, so the server and client markup always match.
  useEffect(() => {
    if (isInShowPeriod() && !sessionStorage.getItem(STORAGE_KEY)) {
      setOpen(true);
    }
  }, []);

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen);
    if (!isOpen) {
      sessionStorage.setItem(STORAGE_KEY, "true");
    }
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] gap-6 overflow-y-auto rounded-2xl p-4 sm:max-w-5xl sm:p-8">
        <DialogHeader className="gap-3 pr-6 text-left">
          <div className="flex items-center gap-2 font-bold text-primary text-sm uppercase tracking-wider">
            <SnowflakeIcon className="size-4" />
            Feiertage
          </div>
          <DialogTitle className="font-bold text-3xl leading-tight sm:text-4xl">
            Öffnungszeiten Weihnachten & Silvester
          </DialogTitle>
          <DialogDescription className="text-base text-foreground/70">
            Über die Feiertage haben wir besondere Öffnungszeiten.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-3">
          {venues.map((venue) => (
            <section
              className="overflow-hidden rounded-2xl shadow-xl"
              key={venue.key}
              style={{ backgroundColor: venue.color, color: venue.foreground }}
            >
              <h3 className="px-5 pt-4 pb-3 font-bold text-xl leading-tight">
                {venue.name}
              </h3>
              <ul className="space-y-2 p-2 pt-0">
                {venue.days.map((day) => (
                  <li
                    className="rounded-xl bg-background px-4 py-3 text-foreground"
                    key={day.date}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-bold">{day.date}</span>
                      {day.hours ? (
                        <span>{day.hours}</span>
                      ) : (
                        <span className="text-foreground/50">Geschlossen</span>
                      )}
                    </div>
                    {day.note ? (
                      <p className="mt-1 text-foreground/70 text-sm">
                        {day.note}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-foreground/70">
            Wir wünschen frohe Feiertage und einen guten Rutsch!
          </p>
          <Button
            className="w-full sm:w-auto"
            onClick={() => handleOpenChange(false)}
            size="lg"
            type="button"
          >
            Verstanden
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
